import { Directive, OnDestroy } from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Applies automatically to every <mat-tab-group>
//
// Tab content stays in the DOM while its tab is inactive, so anything inside that measures its
// own layout on first render (e.g. lcs-responsive-table's column width calculation in
// @lcs/shared/table, which reads the element's real clientWidth) sees a not-yet-visible,
// effectively zero-width element. None of Angular Material's own tab-activation hooks fire a
// real window resize event, and @lcs/shared/table only recalculates column widths in response to
// one (via CDK's ViewportRuler), so without this nudge those measurements get stuck until the
// user manually resizes/zooms the browser. Dispatching a resize event on every tab change lets
// anything like that remeasure itself against the tab's real, now-visible dimensions.
//
// This is a workaround for a gap in @lcs/shared/table, not a fix to it — we don't own that
// library. If it's ever updated to recalculate on visibility change (e.g. via IntersectionObserver
// or a public re-render hook), this directive can go away.
@Directive({
   selector: 'mat-tab-group',
   standalone: false,
})
export class ResizeOnTabChangeDirective implements OnDestroy {
   private destroyed$ = new Subject<void>();

   constructor(private tabGroup: MatTabGroup) {
      this.tabGroup.selectedTabChange.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         window.dispatchEvent(new Event('resize'));
      });
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }
}
